package ui.widget

import androidx.compose.animation.*
import androidx.compose.animation.core.tween
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.*
import org.jetbrains.compose.resources.StringResource
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.Navigation
import tools.rememberString

data class ToolbarAction(
    val icon: ImageVector,
    val visible: State<Boolean> = mutableStateOf(true),
    val tooltip: StringResource? = null,
    val canBeHidden: Boolean = true,
    val onClick: () -> Unit,
) {

    companion object {
        fun delete(tooltip: StringResource = Res.string.toolbar_action_delete, onClick: () -> Unit) =
            ToolbarAction(Svg.Delete_outline, tooltip = tooltip, onClick = onClick)

        fun save(visible: State<Boolean>, tooltip: StringResource = Res.string.toolbar_action_save, onClick: () -> Unit) =
            ToolbarAction(Svg.Content_save, tooltip = tooltip, visible = visible, canBeHidden = false, onClick = onClick)

        fun edit(tooltip: StringResource = Res.string.toolbar_action_edit, onClick: () -> Unit) =
            ToolbarAction(Svg.Edit, tooltip = tooltip, onClick = onClick)

        fun pause(tooltip: StringResource? = null, onClick: () -> Unit) =
            ToolbarAction(Svg.Pause, tooltip = tooltip, onClick = onClick)

        fun resume(tooltip: StringResource? = null, canBeHidden: Boolean = false, onClick: () -> Unit) =
            ToolbarAction(Svg.Triangle, tooltip = tooltip, canBeHidden = canBeHidden, onClick = onClick)

        fun stop(tooltip: StringResource? = null, onClick: () -> Unit) =
            ToolbarAction(Svg.Close, tooltip = tooltip, onClick = onClick)

        fun chart(tooltip: StringResource? = null, onClick: () -> Unit) =
            ToolbarAction(Svg.Chart_line, tooltip = tooltip, onClick = onClick)

        fun more(tooltip: StringResource? = Res.string.toolbar_actions, onClick: () -> Unit) =
            ToolbarAction(Svg.Dots_vertical, tooltip = tooltip, onClick = onClick)
    }
}

@Composable
private fun ToolbarContent(titlePrefix: String, titleSuffix: String) {
    Column {
        Text(
            text = titlePrefix.trim(),
            style = MaterialTheme.typography.titleMedium,
            minLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        if (titleSuffix.isNotEmpty()) {
            SelectionContainer {
                Text(
                    text = titleSuffix,
                    fontWeight = FontWeight.Thin,
                    style = MaterialTheme.typography.titleSmall,
                    minLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ToolbarSimple(
    titlePrefix: String,
    titleSuffix: String,
    navigationIcon: @Composable () -> Unit,
) {
    TopAppBar(
        modifier = Modifier.fillMaxWidth(),
        colors = TopAppBarDefaults.topAppBarColors(
            containerColor = MaterialTheme.colorScheme.secondaryContainer
        ),
        navigationIcon = { navigationIcon() },
        title = {
            ToolbarContent(titlePrefix, titleSuffix)
        })
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)
@Composable
fun Toolbar(
    nav: Navigation,
    titlePrefix: String,
    titleSuffix: String,
    onUp: (() -> Unit)? = null,
    navigationIcon: @Composable () -> Unit = {
        IconButton({ onUp?.invoke() ?: nav.up() }) {
            Icon(Svg.Arrow_left, null)
        }
    },
    actions: List<ToolbarAction> = listOf()
) {
    rememberCoroutineScope()
    TopAppBar(
        modifier = Modifier.fillMaxWidth(),
        colors = TopAppBarDefaults.topAppBarColors(
            containerColor = MaterialTheme.colorScheme.secondaryContainer
        ),
        navigationIcon = navigationIcon,
        title = {
            ToolbarContent(titlePrefix, titleSuffix)
        },
        actions = {
            var expanded by remember { mutableStateOf(false) }
            DropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false }) {
                actions
                    .filter { it.canBeHidden }
                    .filter { it.visible.value }
                    .forEach { entry ->
                        DropdownMenuItem(
                            onClick = entry.onClick,
                            text = {
                                entry.tooltip?.rememberString()?.also {
                                    Text(it)
                                } ?: run {
                                    Icon(entry.icon, null)
                                }
                            },
                            leadingIcon =
                                if (entry.tooltip == null) null
                                else {
                                    {
                                        Icon(entry.icon, null)
                                    }
                                }
                        )
                    }
            }
            if (LocalDisplayProvider.current.isMobileOrSmall && actions.size > 2) {
                actions.filterNot { it.canBeHidden }
                    .takeIf { it.isNotEmpty() }
                    ?.also { InlineActions(it) }
                InlineActions(
                    listOf(
                        ToolbarAction.more {
                            expanded = !expanded
                        }
                    ))
            } else {
                InlineActions(actions)
            }
        })
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun InlineActions(actions: List<ToolbarAction>) {
    actions.forEach { item ->
        Space(8.dp)
        AnimatedContent(
            targetState = item.visible.value,
            transitionSpec = {
                (fadeIn(animationSpec = tween(220, delayMillis = 90)) + scaleIn(
                    animationSpec = tween(
                        220,
                        delayMillis = 90
                    )
                ))
                    .togetherWith(fadeOut(animationSpec = tween(90)) + scaleOut(animationSpec = tween(90)))
            }) {
            if (it) {
                TooltipBox(
                    positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
                    tooltip = {
                        ElevatedCard {
                            item.tooltip?.rememberString()?.also {
                                Text(it, modifier = Modifier.padding(8.dp))
                            }
                        }
                    },
                    state = CustomTooltipState.rememberState(),
                    focusable = false,
                    enableUserInput = item.tooltip != null
                ) {
                    IconButton({ item.onClick() }) {
                        Icon(item.icon, null)
                    }
                }
            } else {
                Space(16.dp)
            }
        }
    }
}